import type { StateCreator } from 'zustand';
import type { StoreType, StudySlice } from '@/types/store';
import type { Study } from '@bioavail/shared-types';
import { ArmRole } from '@bioavail/shared-types';
import { createStudy, createArm, uid } from '@/data/factory';
import { getPreset } from '@/config/presets';
import { createDefaultStudy } from '@/data/factory';

function withActiveStudy(
  get: () => StoreType,
): Study | undefined {
  const { studies, activeStudyId } = get();
  return studies.find((s) => s.id === activeStudyId);
}

function updateActiveStudy(
  get: () => StoreType,
  set: (partial: Partial<StoreType>) => void,
  mutate: (study: Study) => Study,
): void {
  const active = withActiveStudy(get);
  if (!active) return;
  const updated = { ...mutate(active), updatedAt: new Date().toISOString() };
  const studies = get().studies.map((s) => (s.id === active.id ? updated : s));
  set({ studies });
}

export const createStudySlice: StateCreator<StoreType, [], [], StudySlice> = (set, get) => ({
  activeStudyId: null,
  studies: [],

  createStudy: (input) => {
    const s = get().settings;
    const study = createStudy({
      name: input.name,
      mode: input.mode,
      arms: [
        createArm({
          id: 'arm-test',
          label: 'Test arm',
          role: ArmRole.Test,
          trapezoidalRule: s.defaultTrapezoidalRule,
          blqRule: s.defaultBlqRule,
        }),
        createArm({
          id: 'arm-ref',
          label: 'Reference arm',
          role: ArmRole.Reference,
          trapezoidalRule: s.defaultTrapezoidalRule,
          blqRule: s.defaultBlqRule,
        }),
      ],
      aucInterval: s.defaultAucInterval,
    });
    set({
      studies: [...get().studies, study],
      activeStudyId: study.id,
    });
  },

  setStudyName: (name) => {
    updateActiveStudy(get, set, (s) => ({ ...s, name }));
  },

  setMode: (mode) => {
    updateActiveStudy(get, set, (s) => ({ ...s, mode }));
  },

  setStudyType: (studyType) => {
    updateActiveStudy(get, set, (s) => ({ ...s, studyType }));
  },

  updateArm: (armId, patch) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) => (a.id === armId ? { ...a, ...patch } : a)),
    }));
  },

  setDataPoints: (armId, points) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) =>
        a.id === armId
          ? { ...a, dataPoints: [...points].sort((p, q) => p.time - q.time) }
          : a,
      ),
    }));
  },

  addDataPoint: (armId, point) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) =>
        a.id === armId
          ? {
              ...a,
              dataPoints: [...a.dataPoints, point].sort((p, q) => p.time - q.time),
            }
          : a,
      ),
    }));
  },

  removeDataPoint: (armId, index) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) =>
        a.id === armId
          ? { ...a, dataPoints: a.dataPoints.filter((_, i) => i !== index) }
          : a,
      ),
    }));
  },

  setDirectAuc: (armId, value) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) =>
        a.id === armId
          ? {
              ...a,
              directAuc: value,
              usesDirectAuc: value !== undefined && value > 0,
            }
          : a,
      ),
    }));
  },

  setAucInterval: (interval) => {
    updateActiveStudy(get, set, (s) => ({ ...s, aucInterval: interval }));
  },

  setTrapezoidalRule: (armId, rule) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) => (a.id === armId ? { ...a, trapezoidalRule: rule } : a)),
    }));
  },

  setLambdaZRange: ({ armId, start, end }) => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) =>
        a.id === armId
          ? {
              ...a,
              lambdaZStartIndex: start,
              lambdaZEndIndex: end,
              lambdaZManualOverride: true,
            }
          : a,
      ),
    }));
  },

  setPartialAucWindow: (window) => {
    updateActiveStudy(get, set, (s) => ({ ...s, partialAucWindow: window }));
  },

  setDosingInterval: (tau) => {
    updateActiveStudy(get, set, (s) => ({ ...s, dosingInterval: tau }));
  },

  setBcsClass: (cls) => {
    updateActiveStudy(get, set, (s) => ({ ...s, bcsClass: cls }));
  },

  swapRoles: () => {
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) => ({
        ...a,
        role: a.role === ArmRole.Test ? ArmRole.Reference : ArmRole.Test,
      })),
    }));
  },

  loadPreset: (presetId) => {
    const preset = getPreset(presetId);
    if (!preset) return;
    const built = preset.build();
    const active = withActiveStudy(get);
    if (!active) return;
    const updated: Study = {
      ...active,
      mode: built.mode,
      studyType: built.studyType,
      aucInterval: built.aucInterval,
      arms: built.arms,
      updatedAt: new Date().toISOString(),
    };
    set({
      studies: get().studies.map((s) => (s.id === active.id ? updated : s)),
    });
  },

  importCsv: (armId, parsedData) => {
    const sorted = [...parsedData].sort((p, q) => p.time - q.time);
    updateActiveStudy(get, set, (s) => ({
      ...s,
      arms: s.arms.map((a) => (a.id === armId ? { ...a, dataPoints: sorted } : a)),
    }));
  },

  saveStudy: () => {
    const active = withActiveStudy(get);
    if (!active) return;
    set({
      studies: get().studies.map((s) =>
        s.id === active.id ? { ...active, updatedAt: new Date().toISOString() } : s,
      ),
    });
    get().pushToast('Study saved.', 'info');
  },

  loadStudy: (studyId) => {
    set({ activeStudyId: studyId });
    // `loadStudy` only changes activeStudyId, leaving the `studies` array
    // reference unchanged, so the recompute middleware won't detect it.
    // Trigger compute explicitly to avoid showing the previous study's
    // results (finding D1).
    get().compute();
  },

  deleteStudy: (studyId) => {
    const remaining = get().studies.filter((s) => s.id !== studyId);
    let activeStudyId = get().activeStudyId;
    if (activeStudyId === studyId) {
      activeStudyId = remaining.length > 0 ? remaining[0]!.id : null;
    }
    if (remaining.length === 0) {
      // Re-create default study if everything is deleted.
      const fresh = createDefaultStudy();
      set({ studies: [fresh], activeStudyId: fresh.id });
    } else {
      set({ studies: remaining, activeStudyId });
    }
  },

  loadExample: (study) => {
    const newStudy: Study = {
      ...study,
      id: uid('study'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    set({
      studies: [...get().studies, newStudy],
      activeStudyId: newStudy.id,
    });
  },

  loadDemoDataset: (study) => {
    // Demo datasets are loaded as fresh studies with new ids/timestamps so
    // they can be loaded multiple times without id collisions.
    const newStudy: Study = {
      ...study,
      id: uid('study'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    set({
      studies: [...get().studies, newStudy],
      activeStudyId: newStudy.id,
    });
    get().pushToast(`Loaded demo dataset: ${study.name}`, 'info');
  },

  clearDemoData: () => {
    // Reset to a single fresh default study — matches the first-load
    // initialization contract in P1.3.1.
    const fresh = createDefaultStudy();
    set({
      studies: [fresh],
      activeStudyId: fresh.id,
      output: null,
      lastError: null,
      trail: [],
    });
    get().pushToast('Cleared all study data.', 'info');
  },
});
